import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { formatStatus, STATUS_OPTIONS } from '../utils/formatStatus';
import '../styles/AdminOrderDetails.css';
import Toast from './Toast';
import getImgSrc from '../utils/getImgSrc';
import ButtonRegular from './ui/ButtonRegular';

function AdminOrderDetails() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [order, setOrder] = useState(null);
	const [loading, setLoading] = useState(false);
	const [updating, setUpdating] = useState(false);
	const [status, setStatus] = useState('');
	const [toast, setToast] = useState('');

	const apiUrl = process.env.REACT_APP_API_URL || '';

	useEffect(() => {
		const fetchOrder = async () => {
			setLoading(true);
			try {
				const res = await axios.get(`${apiUrl}/api/orders/${id}`, {
					headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
				});
				setOrder(res.data);
				setStatus(res.data.status || 'pending');
			} catch (err) {
				console.error('Błąd pobierania zamówienia:', err);
				setToast('Nie udało się pobrać zamówienia.');
			} finally {
				setLoading(false);
			}
		};

		fetchOrder();
	}, [apiUrl, id]);

	const handleStatusChange = async () => {
		setUpdating(true);
		try {
			const res = await axios.put(`${apiUrl}/api/orders/${id}`, { status }, {
				headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
			});
			setOrder(res.data);
			setToast('Status zaktualizowany.');
		} catch (err) {
			console.error('Błąd aktualizacji statusu:', err);
			setToast('Nie udało się zaktualizować statusu.');
		} finally {
			setUpdating(false);
		}
	};

	const goToInpost = () => {
		const orderNumber = order.orderNumber || order._id;
		navigate('/delivery/inpost', { state: { order } });
	};

	if (loading) return <p>Ładowanie...</p>;
	if (!order) return <p>Brak danych zamówienia.</p>;

	return (
		<div className="admin-order-detail admin-card order-grid">
			<ButtonRegular className="small ghost" onClick={() => navigate(-1)}>‹ Powrót</ButtonRegular>
			<h2 className="order-title">
				Szczegóły zamówienia {order.orderNumber || order._id}
				<span className={`status-badge ${order.status || 'pending'}`}>
					{formatStatus(order.status || 'pending')}
				</span>
			</h2>

			<div className="order-grid-inner">
				<div className="order-main">
					{/* Data zamówienia */}
					<div className="section small">
						<strong>Data:</strong> {order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}
					</div>

					{/* Dane użytkownika */}
					<div className="section card-block">
						<h3>Dane konta</h3>
						{order.user ? (
							<div className="info-grid">
								<div><strong>Imię:</strong> {order.user.firstName || '—'}</div>
								<div><strong>Nazwisko:</strong> {order.user.lastName || '—'}</div>
								<div><strong>Email:</strong> {order.user.email || '—'}</div>
								<div><strong>Telefon:</strong> {order.user.phone || '—'}</div>
								<div><strong>ID:</strong> {order.user._id || '—'}</div>
							</div>
						) : (
							<p>Brak powiązanego konta (zamówienie gość lub konto zostało usunięte)</p>
						)}

						{/* Adresy użytkownika */}
						{order.user?.addresses?.length > 0 && (
							<div className="info-grid">
								<h4>Adresy użytkownika</h4>
								{order.user.addresses.map((addr, idx) => (
									<div key={idx} style={{ marginBottom: 10 }}>
										<div><strong>Ulica:</strong> {addr.street || '—'}</div>
										<div><strong>Miasto:</strong> {addr.city || '—'}</div>
										<div><strong>Kod pocztowy:</strong> {addr.postalCode || '—'}</div>
										<div><strong>Kraj:</strong> {addr.country || '—'}</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Dane klienta */}
					<div className="section card-block">
						<h3>Dane klienta</h3>
						<div className="info-grid">
							<div><strong>Imię:</strong> {order.clientAddress?.firstName || '—'}</div>
							<div><strong>Nazwisko:</strong> {order.clientAddress?.lastName || '—'}</div>
							<div><strong>Telefon:</strong> {order.clientAddress?.phone || '—'}</div>
							<div><strong>Ulica:</strong> {order.clientAddress?.street || '—'}</div>
							<div><strong>Numer budynku:</strong> {order.clientAddress?.building || '—'}</div>
							<div><strong>Mieszkanie:</strong> {order.clientAddress?.apartment || '—'}</div>
							<div><strong>Miasto:</strong> {order.clientAddress?.city || '—'}</div>
							<div><strong>Kod pocztowy:</strong> {order.clientAddress?.postalCode || '—'}</div>
							<div><strong>Kraj:</strong> {order.clientAddress?.country || '—'}</div>
						</div>
					</div>

					{/* Adres dostawy */}
					<div className="section card-block">
						<h3>Adres dostawy</h3>
						<div className="info-grid">
							<div><strong>Miasto:</strong> {order.shippingAddress?.city || '—'}</div>
							<div><strong>Kod pocztowy:</strong> {order.shippingAddress?.postalCode || '—'}</div>
							<div><strong>Ulica:</strong> {order.shippingAddress?.street || '—'}</div>
							<div><strong>Numer budynku:</strong> {order.shippingAddress?.building || '—'}</div>
							<div><strong>Mieszkanie:</strong> {order.shippingAddress?.apartment || '—'}</div>
							<div><strong>Kraj:</strong> {order.shippingAddress?.country || '—'}</div>
						</div>
					</div>

					{/* Pozycje zamówienia */}
					<div className="section card-block">
						<h3>Pozycje</h3>
						<div className="order-items-list">
							<div className="order-items-header">
								<div className="col-image">Produkt</div>
								<div className="col-name">Nazwa</div>
								<div className="col-qty">Ilość</div>
								<div className="col-unit">Cena jedn.</div>
								<div className="col-sub">Razem</div>
							</div>
							{(order.items || []).map((item, idx) => {
								const unit = (item.price || 0).toFixed(2);
								const qty = item.quantity || 1;
								const subtotal = (parseFloat(item.price || 0) * qty).toFixed(2);
								const img = item.image || item.product?.image || item.product?.images?.[0];
								const sku = item.sku || item.product?.sku || '';
								const opts = item.variant || item.options || '';

								return (
									<div className="order-item" key={item._id || idx}>
										<div className="col-image">
											{img ? <img src={getImgSrc(img)} alt={item.name} className="item-image" /> : <div className="item-image placeholder" />}
										</div>
										<div className="col-name">
											<div className="item-name">{item.name || item.product?.name || 'Produkt'}</div>
											<div className="item-meta">{sku} {opts ? `• ${opts}` : ''}</div>
										</div>
										<div className="col-qty">{qty}</div>
										<div className="col-unit">{unit} PLN</div>
										<div className="col-sub">{subtotal} PLN</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>

						{/* Informacje o dostawie */}
						<div className="section card-block">
							<h3>Informacje o dostawie</h3>

							<p><strong>Metoda dostawy:</strong> {order.delivery?.label || '—'}</p>
							{order.payment?.status === 'paid' && order.delivery?.method === 'inpost' && (
								<div style={{ marginTop: 10 }}>
									<ButtonRegular
										className="small"
										onClick={goToInpost}
										ariaLabel="Zamów kuriera InPost"
									>
										Zamów kuriera InPost
									</ButtonRegular>
								</div>
							)}
						</div>

				{/* Panel boczny */}
				<aside className="order-side">
					<div className="section card-block sticky-side">
						<h3>Informacje o płatności</h3>
						<p><strong>Metoda płatności:</strong> {order.payment.method || '—'}</p>
						<p><strong>Status płatności:</strong> {formatStatus(order.payment.status || 'pending')}</p>
						<div>
							<strong>Faktura:</strong>
							{order.invoice ? (
								<div className="info-grid" style={{ marginTop: 8 }}>
									<div><strong>Nazwa firmy:</strong> {order.invoice.companyName || '—'}</div>
									<div><strong>NIP:</strong> {order.invoice.taxId || '—'}</div>
									<div><strong>Ulica:</strong> {order.invoice.street || '—'}</div>
									<div><strong>Numer budynku:</strong> {order.invoice.building || '—'}</div>
									<div><strong>Mieszkanie:</strong> {order.invoice.apartment || '—'}</div>
									<div><strong>Miasto:</strong> {order.invoice.city || '—'}</div>
									<div><strong>Kod pocztowy:</strong> {order.invoice.postalCode || '—'}</div>
									<div><strong>Kraj:</strong> {order.invoice.country || '—'}</div>
								</div>
							) : (
								<p>Brak danych faktury</p>
							)}
						</div>
					</div>

					{/* Status zamówienia */}
					<div className="section card-block">
						<h3>Status</h3>
						<div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 16 }}>
							<select
								style={{
									textAlign: 'left',
									fontSize: '1rem',
									color: '#4b2a4b',
									border: '1px solid #4b2a4b',
									padding: '6px 10px',
									borderRadius: 6,
									background: 'white',
									cursor: 'pointer',
									minWidth: 140,
									height: 36,
								}}
								value={status}
								onChange={e => setStatus(e.target.value)}
							>
								{STATUS_OPTIONS.map(opt => (
									<option key={opt.value} value={opt.value}>{opt.label}</option>
								))}
							</select>

							<ButtonRegular className="small" onClick={handleStatusChange} disabled={updating} ariaLabel="Zmień status">
								{updating ? 'Aktualizowanie...' : 'Zmień status'}
							</ButtonRegular>
						</div>
					</div>

					{/* Podsumowanie */}
					<div className="section card-block order-summary">
						<h3>Podsumowanie</h3>
						<div className="summary-row"><span>Razem:</span><strong>{(order.total || 0).toFixed(2)} PLN</strong></div>
						<div className="summary-row"><span>Nr zamówienia:</span><span>{order.orderNumber || order._id}</span></div>
						<div className="summary-row"><span>Data:</span><span>{order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}</span></div>
						<div className="summary-row"><span>Status:</span><span>{formatStatus(order.status || 'pending')}</span></div>
					</div>
				</aside>
			</div>

			{toast && <Toast message={toast} onClose={() => setToast('')} />}
		</div>
	);
}

export default AdminOrderDetails;
